import { EditOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client/react';
import { App, Form, Modal } from 'antd';
import type React from 'react';
import { useEffect } from 'react';
import type { AnalysisStationUpdateInput } from '../../../../graphql/generated/graphql';
import type { AnalysisStation } from '../AnalysisStations';
import { AnalysisStationForm } from './AnalysisStationForm';
import { UPDATE_ANALYSIS_STATION_MUTATION } from './UpdateAnalysisStationModal.gql';

interface UpdateAnalysisStationModalProps {
  analysisStation: AnalysisStation | null;
  onSuccess: () => void;
  onClose: () => void;
  analysisStations: AnalysisStation[];
}

export const UpdateAnalysisStationModal: React.FC<UpdateAnalysisStationModalProps> = ({
  analysisStation,
  onSuccess,
  onClose,
  analysisStations,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [updateAnalysisStationMutation, { loading }] = useMutation(UPDATE_ANALYSIS_STATION_MUTATION);

  useEffect(() => {
    if (analysisStation) {
      form.setFieldsValue({
        name: analysisStation.name ?? '',
        description: analysisStation.description ?? '',
      });
    }
  }, [analysisStation, form]);

  const closeAndReset = () => {
    form.resetFields();
    onClose();
  };

  const nameValidator = (name: string) => {
    return !analysisStations.filter(d => d.id !== analysisStation?.id).some(d => d.name.toLowerCase() === name.toLowerCase());
  };

  const update = async (values: AnalysisStationUpdateInput) => {
    try {
      await updateAnalysisStationMutation({
        variables: {
          id: analysisStation?.id ?? '',
          input: {
            name: values.name,
            description: values.description || '',
          },
        },
      });
      message.success('Analysestation wurde aktualisiert');
      onSuccess();
      closeAndReset();
    } catch (_e) {
      message.error('Analysestation konnte nicht aktualisiert werden');
    }
  };

  return (
    <Modal
      title="Analysestation bearbeiten"
      open={!!analysisStation}
      onOk={form.submit}
      onCancel={closeAndReset}
      confirmLoading={loading}
      width={768}
      forceRender
      okText="Speichern"
      cancelText="Abbrechen"
      okButtonProps={{ icon: <EditOutlined /> }}
    >
      <AnalysisStationForm
        form={form}
        initialValues={{
          name: analysisStation?.name ?? '',
          description: analysisStation?.description ?? '',
        }}
        onFinish={values => update(values as AnalysisStationUpdateInput)}
        nameValidator={nameValidator}
      />
    </Modal>
  );
};
